/**
 * 포트폴리오 삭제 추적 훅
 * 
 * 포트폴리오 삭제의 의도-결과를 추적
 * - portfolio_delete_start: 삭제 시작
 * - portfolio_delete_success: 삭제 성공
 * - portfolio_delete_fail: 삭제 실패
 * 
 * @example
 * ```tsx
 * function PortfolioListPage() {
 *   const portfolioDelete = usePortfolioDelete();
 *   
 *   const handleDelete = async (portfolioId: number, fromListId: string) => {
 *     portfolioDelete.start({
 *       portfolio_id: portfolioId,
 *       from_list_id: fromListId,
 *     });
 *     
 *     try {
 *       await deletePortfolioAPI(portfolioId);
 *       portfolioDelete.success();
 *     } catch (error) {
 *       portfolioDelete.fail('backend_5xx');
 *     }
 *   };
 * }
 * ```
 */

import { useIntent } from '../useIntent';
import { track } from '../../../../core/client';

export interface PortfolioDeleteContext {
    portfolio_id: number;
    from_list_id?: string;
}

export interface PortfolioDeleteActions {
    start: (context: PortfolioDeleteContext, options?: {
        parentIntentId?: string;
        parentIntentType?: string;
    }) => string;
    success: () => void;
    fail: (reason?: string) => void;
}

/**
 * 포트폴리오 삭제 추적 훅
 */
export function usePortfolioDelete(): PortfolioDeleteActions {
    const intent = useIntent<PortfolioDeleteContext>({
        type: 'portfolio_delete',
        requiredGate: 'login',
        onStart: ({ intent_id, context, parent_intent_id, required_gate }) => {
            track('portfolio_delete_start', {
                intent_id,
                portfolio_id: context!.portfolio_id,
                from_list_id: context!.from_list_id,
                parent_intent_id,
                required_gate,
            });
        },
        onSuccess: ({ intent_id, context, latency_ms }) => {
            track('portfolio_delete_success', {
                intent_id,
                portfolio_id: context!.portfolio_id,
                latency_ms,
            });
        },
        onFail: ({ intent_id, context, fail_reason }) => {
            track('portfolio_delete_fail', {
                intent_id,
                portfolio_id: context!.portfolio_id,
                fail_reason,
            });
        },
    });

    return {
        start: (context, options) => intent.start(context, options),
        success: () => intent.success(),
        fail: (reason) => intent.fail(reason),
    };
}

